import React, { useEffect, useState, useRef, useCallback } from 'react';
import { AiOutlineClose } from "react-icons/ai";
import useOutsideClick from "hooks/click-outside-hook";
import useHostManagement from "containers/host-management/useHooks";
import useStore from "hooks/useStore";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";

interface TicketData {
  id?: number;
  reason?: string;
  penaltyAmount?: number;
  status?: string;
  adminNotes?: string;
}

interface Message {
  id: number;
  ticketId: number;
  senderId: number;
  message: string;
  messageType?: string;
  createdAt: string;
  sender?: {
    id?: number;
    firstName?: string;
    lastName?: string;
    profilePicId?: number;
  };
}

interface ChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: TicketData | null;
  onBack?: () => void;
}

const ChatModal: React.FC<ChatModalProps> = ({
  isOpen,
  onClose,
  ticket,
  onBack,
}) => {
  const modalRef = useOutsideClick(onClose);
  const chatRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isLoadingRef = useRef(false);
  const scrollPositionRef = useRef<number>(0);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState<string>("");
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [hasMoreMessages, setHasMoreMessages] = useState<boolean>(true);
  const [shouldAutoScroll, setShouldAutoScroll] = useState<boolean>(true);
  const { getChatTicketMessages, postChatTicketMessages } = useHostManagement();
  const { getUser } = useStore();
  const userData = getUser();
  const myUserId = userData?.id;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const fetchInitialMessages = async () => {
    if (!ticket?.id || isLoadingRef.current) return;
    
    isLoadingRef.current = true;
    setIsLoading(true);
    setCurrentPage(0);
    setHasMoreMessages(true);
    
    try {
      console.log("Fetching initial chat messages for ticket ID:", ticket.id);
      const response = await getChatTicketMessages(
        ticket.id,
        (data: any) => {
          console.log("Chat messages response:", data);
          let messageList: Message[] = [];
          
          // Handle API response structure: { status: true, data: { content: [...], last: boolean, totalPages: number, pageNumber: string } }
          if (data?.content && Array.isArray(data.content)) {
            messageList = data.content;
            // Use totalPages and pageNumber to determine if there are more pages
            const currentPageNum = parseInt(data.pageNumber || "0", 10);
            const totalPagesNum = parseInt(data.totalPages || "1", 10);
            setHasMoreMessages(currentPageNum < totalPagesNum - 1);
          } else if (data?.data?.content && Array.isArray(data.data.content)) {
            messageList = data.data.content;
            // Use totalPages and pageNumber from data.data
            const currentPageNum = parseInt(data.data.pageNumber || "0", 10);
            const totalPagesNum = parseInt(data.data.totalPages || "1", 10);
            setHasMoreMessages(currentPageNum < totalPagesNum - 1);
            console.log("Initial load - Page:", currentPageNum, "Total Pages:", totalPagesNum, "Has More:", currentPageNum < totalPagesNum - 1);
          } else if (Array.isArray(data)) {
            messageList = data;
            // If it's a plain array, assume there might be more if we got 10 items
            setHasMoreMessages(data.length === 10);
          } else if (data?.messages && Array.isArray(data.messages)) {
            messageList = data.messages;
            setHasMoreMessages(false); // Can't determine pagination from this structure
          } else {
            console.warn("Unexpected response structure:", data);
            messageList = [];
            setHasMoreMessages(false);
          }
          
          // Remove duplicates and set messages
          const uniqueMessages = messageList.filter((message, index, arr) => 
            arr.findIndex(m => m.id === message.id) === index
          );
          setMessages(uniqueMessages);
          setShouldAutoScroll(true); // Auto-scroll after initial load
        },
        { page: 0, size: 10 }
      );
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  };

  const fetchOlderMessages = async () => {
    if (!ticket?.id || isLoadingRef.current || !hasMoreMessages || isLoadingMore) return;
    
    isLoadingRef.current = true;
    setIsLoadingMore(true);
    setShouldAutoScroll(false); // Don't auto-scroll when loading older messages
    
    // Save scroll position before loading
    if (chatRef.current) {
      scrollPositionRef.current = chatRef.current.scrollHeight;
    }
    
    try {
      const nextPage = currentPage + 1;
      console.log("Fetching older messages - Page:", nextPage);
      
      const response = await getChatTicketMessages(
        ticket.id,
        (data: any) => {
          let newMessages: Message[] = [];
          
          // Handle API response structure
          if (data?.content && Array.isArray(data.content)) {
            newMessages = data.content;
            // Use totalPages and pageNumber to determine if there are more pages
            const currentPageNum = parseInt(data.pageNumber || "0", 10);
            const totalPagesNum = parseInt(data.totalPages || "1", 10);
            setHasMoreMessages(currentPageNum < totalPagesNum - 1);
          } else if (data?.data?.content && Array.isArray(data.data.content)) {
            newMessages = data.data.content;
            // Use totalPages and pageNumber from data.data
            const currentPageNum = parseInt(data.data.pageNumber || "0", 10);
            const totalPagesNum = parseInt(data.data.totalPages || "1", 10);
            setHasMoreMessages(currentPageNum < totalPagesNum - 1);
            console.log("Fetch older - Page:", currentPageNum, "Total Pages:", totalPagesNum, "Has More:", currentPageNum < totalPagesNum - 1);
          } else if (Array.isArray(data)) {
            newMessages = data;
            setHasMoreMessages(data.length === 10);
          }
          
          if (newMessages.length > 0) {
            setMessages((prev) => {
              // Remove duplicates
              const existingIds = new Set(prev.map((m) => m.id));
              const filtered = newMessages.filter((m: Message) => !existingIds.has(m.id));
              
              if (filtered.length > 0) {
                // Prepend older messages at the beginning
                const updated = [...filtered, ...prev];
                return updated.filter((message, index, arr) => 
                  arr.findIndex(m => m.id === message.id) === index
                );
              }
              return prev;
            });
            setCurrentPage(nextPage);
          } else {
            setHasMoreMessages(false);
          }
        },
        { page: nextPage, size: 10 }
      );
    } catch (error) {
      console.error("Error fetching older messages:", error);
    } finally {
      setIsLoadingMore(false);
      isLoadingRef.current = false;
      
      // Restore scroll position after loading
      requestAnimationFrame(() => {
        if (chatRef.current && scrollPositionRef.current > 0) {
          const newScrollHeight = chatRef.current.scrollHeight;
          const scrollDifference = newScrollHeight - scrollPositionRef.current;
          chatRef.current.scrollTop = scrollDifference;
          scrollPositionRef.current = 0;
        }
      });
    }
  };

  const pollForNewMessages = useCallback(async () => {
    if (!ticket?.id || isLoadingRef.current || isSending) return;
    
    isLoadingRef.current = true;
    
    try {
      // Poll only for latest messages (page 0, size 10)
      const response = await getChatTicketMessages(
        ticket.id,
        (data: any) => {
          let newMessages: Message[] = [];
          
          // Handle API response structure
          if (data?.content && Array.isArray(data.content)) {
            newMessages = data.content;
          } else if (data?.data?.content && Array.isArray(data.data.content)) {
            newMessages = data.data.content;
          } else if (Array.isArray(data)) {
            newMessages = data;
          } else if (data?.messages && Array.isArray(data.messages)) {
            newMessages = data.messages;
          }

          if (newMessages.length > 0) {
            setMessages((prev) => {
              const existingIds = new Set(prev.map((m) => m.id));
              const filtered = newMessages.filter((m: Message) => !existingIds.has(m.id));
              
              if (filtered.length > 0) {
                // Append new messages at the end (they are latest)
                const updated = [...prev, ...filtered];
                // Remove duplicates by ID
                const unique = updated.filter((message, index, arr) => 
                  arr.findIndex(m => m.id === message.id) === index
                );
                // Only auto-scroll if we're at the bottom (user hasn't scrolled up)
                if (chatRef.current) {
                  const { scrollTop, scrollHeight, clientHeight } = chatRef.current;
                  const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
                  setShouldAutoScroll(isNearBottom);
                } else {
                  setShouldAutoScroll(true);
                }
                return unique;
              }
              return prev;
            });
          }
        },
        { page: 0, size: 10 }
      );
    } catch (error) {
      console.error("Error polling for new messages:", error);
    } finally {
      isLoadingRef.current = false;
    }
  }, [ticket?.id, isSending]);

  const handleScroll = () => {
    if (!chatRef.current || isLoadingRef.current || !hasMoreMessages || isLoadingMore) return;

    const { scrollTop, scrollHeight, clientHeight } = chatRef.current;
    const threshold = 100; // Load when 100px from top

    // Check if user is near bottom - if so, enable auto-scroll for new messages
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    if (isNearBottom) {
      setShouldAutoScroll(true);
    }

    // Load older messages when scrolling near top
    if (scrollTop < threshold) {
      fetchOlderMessages();
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setMessages([]);
      setNewMessage("");
      setCurrentPage(0);
      setHasMoreMessages(true);
      setShouldAutoScroll(true);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && ticket?.id) {
      fetchInitialMessages();
      // Start polling every 10 seconds - only set once
      if (!intervalRef.current) {
        intervalRef.current = setInterval(() => {
          pollForNewMessages();
        }, 10000);
      }
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ticket?.id]);

  useEffect(() => {
    if (chatRef.current && messages.length > 0 && !isLoading && shouldAutoScroll) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        if (chatRef.current) {
          chatRef.current.scrollTo({
            top: chatRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
      }, 0);
    }
  }, [messages.length, isLoading, shouldAutoScroll]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !ticket?.id || isSending) return;

    const messageText = newMessage.trim();
    setNewMessage("");
    setIsSending(true);

    const tempMessage: Message = {
      id: Date.now(),
      ticketId: ticket.id!,
      senderId: userData?.id || 0,
      message: messageText,
      messageType: "text",
      createdAt: new Date().toISOString(),
      sender: {
        id: userData?.id,
        firstName: userData?.firstName,
        lastName: userData?.lastName,
        profilePicId: userData?.profilePicId,
      },
    };

    setMessages((prev) => [...prev, tempMessage]);
    setShouldAutoScroll(true); // Auto-scroll after sending message

    try {
      console.log("Sending message to ticket ID:", ticket.id, "Message:", messageText);
      const response = await postChatTicketMessages(ticket.id, {
        message: messageText,
      });
      console.log("Send message response:", response);
      if (response?.status) {
        // Refresh messages after sending
        await pollForNewMessages();
      } else {
        setMessages((prev) => prev.filter((msg) => msg.id !== tempMessage.id));
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prev) => prev.filter((msg) => msg.id !== tempMessage.id));
    } finally {
      setIsSending(false);
    }
  };

  const formatTime = (dateStr: string): string => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const formatCurrency = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined) return "$0.00";
    return `$${amount.toFixed(2)}`;
  };

  const getStatusLabel = (status?: string): string => {
    if (!status) return "";
    if (status === "approved" && ticket?.penaltyAmount) {
      return "Approved (With Penalty)";
    }
    if (status === "approved") {
      return "Approved (Without Penalty)";
    }
    return status
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const getStatusColor = (status?: string): string => {
    if (status === "pending") return "bg-yellow-100 text-yellow-800";
    if (status === "approved") return "bg-green-100 text-green-800";
    if (status === "rejected") return "bg-red-100 text-red-800";
    return "bg-gray-100 text-gray-800";
  };

  if (!isOpen || !ticket) {
    return null;
  }

  return (
    <div 
      className="fixed z-50 inset-0 bg-black bg-opacity-50 flex justify-center items-center transition-opacity duration-300"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={modalRef}
        className="relative bg-white rounded-lg shadow-lg max-w-md w-[90%] max-h-[90vh] flex flex-col transform transition-transform duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with Back Button */}
        <div className="flex items-center justify-between p-4 border-b border-mediumGrayBorder">
          <button
            onClick={onBack || onClose}
            className="text-gray-500 hover:text-gray-800 transition"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h2 className="text-lg font-bold text-darkCharcoalText">Ticket #{ticket?.id}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 transition"
          >
            <AiOutlineClose size={20} />
          </button>
        </div>

        {/* Ticket Details Card */}
        <div className="p-4 bg-white border-b border-mediumGrayBorder">
          <div className="bg-white rounded-lg p-4 border border-lightGrayBorder">
            {/* Ticket Status */}
            <div className="mb-3">
              <p className="text-sm text-gray-600 mb-2 font-medium">Ticket Status</p>
              <span className={`inline-block px-3 py-1 rounded-lg text-sm font-semibold ${getStatusColor(ticket?.status)}`}>
                {getStatusLabel(ticket?.status)}
              </span>
            </div>

            {/* Reason for Cancellation */}
            <div className="mb-3">
              <p className="text-sm text-gray-600 mb-2 font-medium">Reason for Cancellation:</p>
              <p className="text-sm text-primaryText">
                {ticket?.reason || "-"}
              </p>
            </div>

            {/* Admin Notes */}
            {ticket?.adminNotes && (
              <div className="mb-3">
                <p className="text-sm text-gray-600 mb-2 font-medium">Admin Notes:</p>
                <p className="text-sm text-primaryText">
                  {ticket.adminNotes}
                </p>
              </div>
            )}

            {/* Penalty Amount */}
            {ticket?.penaltyAmount && (
              <div>
                <p className="text-sm text-gray-600 mb-2 font-medium">Penalty Amount:</p>
                <p className="text-base font-semibold text-orange-600">
                  {formatCurrency(ticket.penaltyAmount)}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Chat Messages */}
        <div
          ref={chatRef}
          className="flex-1 overflow-y-auto p-4 bg-gray-50 scroll-smooth"
          style={{ maxHeight: 'calc(90vh - 300px)' }}
          onScroll={handleScroll}
        >
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-darkTealBlue border-t-transparent"></div>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center text-gray-500 text-sm py-8">
              No messages yet. Start the conversation!
            </div>
          ) : (
            <div className="space-y-3">
              {isLoadingMore && (
                <div className="flex justify-center items-center py-2">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-darkTealBlue border-t-transparent"></div>
                    <span>Loading older messages...</span>
                  </div>
                </div>
              )}
              {messages
                .filter((message, index, arr) => 
                  arr.findIndex(m => m.id === message.id) === index
                )
                .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
                .map((msg) => {
                const sentByMe = msg.senderId === myUserId || (msg.sender?.id === myUserId);
                const userName = msg.sender 
                  ? `${msg.sender.firstName || ''} ${msg.sender.lastName || ''}`.trim() || "User"
                  : "Admin";
                const userInitials = userName
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()
                  .slice(0, 2);
                const profilePic = null; // TODO: Fetch profile pic URL from profilePicId if needed

                return (
                  <div
                    key={msg.id}
                    className={`flex ${sentByMe ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`flex gap-2 max-w-[80%] ${sentByMe ? 'flex-row-reverse' : 'flex-row'}`}>
                      {!sentByMe && (
                        <div className="w-8 h-8 rounded-full bg-darkTealBlue flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                          {profilePic ? (
                            <img src={profilePic} alt={userName} className="w-full h-full rounded-full object-cover" />
                          ) : (
                            userInitials
                          )}
                        </div>
                      )}
                      <div className={`rounded-lg px-3 py-2 ${sentByMe ? 'bg-darkTealBlue text-white' : 'bg-white border border-lightGrayBorder'}`}>
                        {!sentByMe && (
                          <p className="text-xs font-semibold mb-1 text-gray-700">{userName}</p>
                        )}
                        <p className={`text-sm ${sentByMe ? 'text-white' : 'text-primaryText'}`}>
                          {msg.message}
                        </p>
                        <p className={`text-xs mt-1 ${sentByMe ? 'text-blue-100' : 'text-gray-500'}`}>
                          {formatTime(msg.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Message Input */}
        <div className="border-t border-mediumGrayBorder bg-white p-4">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSendMessage();
            }}
            className="flex items-center gap-2"
          >
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              className="flex-1 h-10 px-4 border border-lightGrayBorder rounded-lg outline-none focus:border-darkTealBlue focus:ring-1 focus:ring-darkTealBlue"
              disabled={isSending}
            />
            <button
              type="submit"
              disabled={!newMessage.trim() || isSending}
              className="bg-darkTealBlue hover:bg-teal-700 disabled:bg-gray-300 text-white p-2 rounded-full transition-colors duration-200 disabled:cursor-not-allowed"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-5 h-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                />
              </svg>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChatModal;

